import { FC, RefAttributes } from 'react';
import classNames from 'classnames';
import { ArchiveIcon, DownloadIcon } from '@heroicons/react/outline';

import Button from '../../../components/Button';
import InputGroup from '../../../components/InputGroup';
import ErrorMessage from '../../../components/ErrorMessage';

type PanelData = {
  label: string;
  readOnly?: boolean;
  errorMessage?: string;
  type?: 'text' | 'date' | 'select' | 'number';
  options?: readonly string[];
  value?: string;
  disabled?: boolean;
  IconRight?: React.ComponentType<{ className?: string }> | null;
  iconRightOnClick?: () => void;
  iconRightClassName?: string;
  customComponent?: React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register?: any;
} & Partial<RefAttributes<HTMLInputElement>>;

const PanelRow: FC<PanelData> = ({
  label,
  readOnly = false,
  errorMessage,
  type = 'text',
  options = [],
  value,
  disabled = false,
  IconRight,
  iconRightOnClick,
  iconRightClassName,
  customComponent,
  register = {},
  ...rest
}) => {
  const renderInput = () => {
    if (customComponent) {
      return customComponent;
    }

    if (type === 'select' && options.length > 0) {
      return (
        <select
          {...register}
          {...rest}
          className={classNames(
            'block w-full rounded border-2 border-transparent bg-neutral-50 py-3 px-4 text-body transition-colors',
            'text-neutral-150 focus:border-secondary-50 focus:outline-none focus:ring-0',
            'disabled:bg-transparent disabled:text-caption disabled:font-medium disabled:text-green-150',
            readOnly && 'px-0',
            errorMessage && 'border-red-50 focus:border-red-50',
          )}
        >
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }

    return (
      <InputGroup
        inputSize="small"
        label={label}
        labelHidden
        type={type}
        value={value}
        disabled={disabled}
        readOnly={readOnly}
        IconRight={IconRight}
        iconRightOnClick={iconRightOnClick}
        iconRightClassName={iconRightClassName}
        {...register}
        {...rest}
        className={classNames(
          'disabled:bg-transparent disabled:text-caption disabled:font-medium disabled:text-green-150',
          readOnly && 'px-0',
        )}
      />
    );
  };

  if (customComponent) {
    return <>{customComponent}</>;
  }

  return (
    <div className="mb-2 flex w-full flex-row items-center justify-between">
      <div className="text-caption text-neutral-125">{label}</div>
      <div className="flex w-3/5 min-w-[300px] flex-col mdlg:min-w-[200px]">
        {renderInput()}
        {errorMessage && (
          <ErrorMessage className="mt-2">{errorMessage}</ErrorMessage>
        )}
      </div>
    </div>
  );
};

const Panel: FC<{
  title: string;
  data?: PanelData[];
  onMobileInviteButtonClick?: () => void;
  hasArchiveClient?: boolean;
  setIsDeactivatePatientModalOpen?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  hasExportPrivateNotes?: boolean;
  onExportPrivateNotesClick?: () => void;
  isExportingPrivateNotes?: boolean;
  hasNoPrivateNotes?: boolean;
}> = ({
  title,
  data,
  onMobileInviteButtonClick,
  hasArchiveClient,
  setIsDeactivatePatientModalOpen,
  hasExportPrivateNotes,
  onExportPrivateNotesClick,
  isExportingPrivateNotes,
  hasNoPrivateNotes,
}) => {
  return (
    <div className="mt-4 flex w-full flex-col items-start justify-start rounded-md border border-neutral-50 px-6 pb-3 pt-6">
      <span className="mb-3 text-body font-medium text-green-125">{title}</span>
      {data?.map((panelInfo, index) => {
        return <PanelRow key={index} {...panelInfo} />;
      })}
      {onMobileInviteButtonClick && (
        <Button
          title={`Send mobile app invite`}
          size="small"
          theme="secondary"
          onClick={onMobileInviteButtonClick}
        />
      )}
      {hasArchiveClient && (
        <div className="my-4 w-full">
          <Button
            title="Archive client"
            noBackground
            noOutline
            size="small"
            theme="secondary"
            className="text-caption font-bold"
            IconComponent={ArchiveIcon}
            iconClassName="text-secondary-100"
            onClick={() => setIsDeactivatePatientModalOpen?.(true)}
          />
        </div>
      )}
      {hasExportPrivateNotes && (
        <div className="my-4 w-full">
          <Button
            title={
              isExportingPrivateNotes
                ? 'Exporting private notes...'
                : 'Export private notes'
            }
            noBackground
            noOutline
            size="small"
            theme="secondary"
            className="text-caption font-bold"
            IconComponent={DownloadIcon}
            iconClassName="text-secondary-100"
            onClick={onExportPrivateNotesClick}
            disabled={isExportingPrivateNotes || hasNoPrivateNotes}
          />
        </div>
      )}
    </div>
  );
};

export default Panel;
